import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import {
  extractAndNormalizeDeepLinkFromNotification,
  setPendingRedeemNavigationPath,
} from "../utils/deepLinkUtils";
import { logEvent, logException } from "@um/ui";
import { deviceInfoService } from "@um/ui";
import { sendConversionEvent } from "./social";
import { stampNotificationOpened } from "../quests/questStorage";

class FirebaseMessagingService {
  private static instance: FirebaseMessagingService;
  private listeners: {
    tokenListener: PluginListenerHandle | null;
    notificationListener: PluginListenerHandle | null;
    notificationActionListener: PluginListenerHandle | null;
  } = {
    tokenListener: null,
    notificationListener: null,
    notificationActionListener: null,
  };

  private currentUserId: string | null = null;
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  private isAuthInitializing = false;

  private constructor(userId: string | null = null) {
    this.currentUserId = userId;
    this.initializationPromise = this.initializeAsync();
  }

  private async initializeAsync(): Promise<void> {
    if (this.isInitialized) {
      console.log("[FirebaseMessagingService] Already initialized, skipping");
      return;
    }

    if (!Capacitor.isNativePlatform()) {
      console.log("[FirebaseMessagingService] Not on native platform");
      return;
    }

    if (!FirebaseMessaging) {
      console.log(
        "[FirebaseMessagingService] FirebaseMessaging plugin not available"
      );
      return;
    }

    console.log("[FirebaseMessagingService] Initializing...");

    try {
      // Clear existing listeners
      await this.clearListeners();

      // Create new listeners
      await this.createListeners();

      this.isInitialized = true;
      console.log("[FirebaseMessagingService] Initialized successfully");
    } catch (error) {
      logException(error);
      console.error("[FirebaseMessagingService] Initialization failed:", error);
    }
  }

  public static getInstance(
    userId: string | null = null
  ): FirebaseMessagingService {
    if (!FirebaseMessagingService.instance) {
      FirebaseMessagingService.instance = new FirebaseMessagingService(userId);
    }
    return FirebaseMessagingService.instance;
  }

  // Wait for initialization to complete
  private async ensureInitialized(): Promise<void> {
    if (this.initializationPromise) {
      await this.initializationPromise;
    }
  }

  private async clearListeners(): Promise<void> {
    console.log("[FirebaseMessagingService] Clearing existing listeners...");

    // Remove all existing listeners
    if (this.listeners.tokenListener) {
      await this.listeners.tokenListener.remove();
      this.listeners.tokenListener = null;
    }

    if (this.listeners.notificationListener) {
      await this.listeners.notificationListener.remove();
      this.listeners.notificationListener = null;
    }

    if (this.listeners.notificationActionListener) {
      await this.listeners.notificationActionListener.remove();
      this.listeners.notificationActionListener = null;
    }

    console.log("[FirebaseMessagingService] Listeners cleared");
  }

  private async createListeners(): Promise<void> {
    console.log("[FirebaseMessagingService] Creating listeners...");

    // Token listener - handles token refresh and saves to Firestore
    this.listeners.tokenListener = await FirebaseMessaging.addListener(
      "tokenReceived",
      async (event) => {
        console.log("[FirebaseMessagingService] Token received:", event.token);

        if (event.token && this.currentUserId) {
          try {
            await this.saveTokenToFirestore(event.token, this.currentUserId);
            console.log("[FirebaseMessagingService] Token saved to Firestore");
          } catch (error) {
            logException(error);
            console.error(
              "[FirebaseMessagingService] Error saving token:",
              error
            );
          }
        }
      }
    );

    // Notification received listener - just logs
    // this.listeners.notificationListener = await FirebaseMessaging.addListener(
    //   "notificationReceived",
    //   (event) => {
    //     console.log(
    //       "[FirebaseMessagingService] Notification received:",
    //       event.notification
    //     );
    //     alert("Notification received");
    //   }
    // );

    // Notification action listener - handles deep links
    this.listeners.notificationActionListener =
      await FirebaseMessaging.addListener(
        "notificationActionPerformed",
        (event) => {
          console.log(
            "[FirebaseMessagingService] Notification action performed:",
            {
              notification: event.notification,
              actionId: event.actionId,
            }
          );
          // Extract and normalize deep link for debugging
          const deepLinkUrl = extractAndNormalizeDeepLinkFromNotification(
            event.notification
          );

          logEvent("um_notification_click", {
            fcmId: event.notification.id,
            notificationId:
              (event.notification.data as any)?.notificationId || null,
            deepLink: deepLinkUrl,
          });

          // A push tap leaves no trace in Firestore, so the "come when I call"
          // quest reads this flag instead.
          stampNotificationOpened();

          // Handle deep link navigation from notification
          const href = extractAndNormalizeDeepLinkFromNotification(
            event.notification
          );
          if (!href) {
            console.log(
              "[FirebaseMessagingService] No deep link found in notification"
            );
            return;
          }

          // Preserve /redeem intent before hard navigation so AppInitializer can
          // skip onboarding resume when pathname updates lag during cold start.
          setPendingRedeemNavigationPath(href);
          window.location.href = href;
        }
      );

    console.log("[FirebaseMessagingService] Listeners created");
  }

  private async saveTokenToFirestore(
    token: string,
    userId: string
  ): Promise<void> {
    // Skip saving for test user IDs
    if (userId.startsWith("test-")) {
      console.log(
        "[FirebaseMessagingService] Skipping Firestore save for test user:",
        userId
      );
      return;
    }

    try {
      // Pass userId to ensure we're saving to the correct user's device document
      await deviceInfoService.updateFCMToken(token, userId);
      console.log(
        "[FirebaseMessagingService] Token saved via DeviceInfoService for user:",
        userId
      );
    } catch (error) {
      logException(error);
      console.error("[FirebaseMessagingService] Failed to save token:", error);
    }
  }

  public async requestPermissionAndSaveToken(userId: string): Promise<boolean> {
    if (!userId) {
      console.error("[FirebaseMessagingService] User ID required");
      return false;
    }

    if (!Capacitor.isNativePlatform()) {
      console.log("[FirebaseMessagingService] Not on native platform");
      return false;
    }

    // Wait for initialization to complete
    await this.ensureInitialized();

    try {
      // Check current permissions first
      const currentPermissions = await FirebaseMessaging.checkPermissions();
      console.log(
        "[FirebaseMessagingService] Current permissions:",
        currentPermissions
      );

      if (currentPermissions.receive === "granted") {
        console.log("[FirebaseMessagingService] Permissions already granted");

        // Get token if we don't have one
        const fcmTokenData = await FirebaseMessaging.getToken();
        if (fcmTokenData?.token) {
          await this.saveTokenToFirestore(fcmTokenData.token, userId);
          this.currentUserId = userId;
          console.log(
            "[FirebaseMessagingService] Token saved for existing permissions"
          );
          return true;
        }
      }

      // Only request permissions if not already granted
      console.log("[FirebaseMessagingService] Requesting permissions...");
      const permissionResult = await FirebaseMessaging.requestPermissions();

      if (permissionResult.receive !== "granted") {
        console.warn(
          "[FirebaseMessagingService] Permission not granted:",
          permissionResult
        );
        return false;
      }

      sendConversionEvent("um_enable_notifications");

      console.log("[FirebaseMessagingService] Getting FCM token...");
      const fcmTokenData = await FirebaseMessaging.getToken();

      if (!fcmTokenData?.token) {
        throw new Error("Invalid FCM token data");
      }

      await this.saveTokenToFirestore(fcmTokenData.token, userId);
      console.log("[FirebaseMessagingService] Token saved successfully");

      // Update the current user ID without re-initializing
      this.currentUserId = userId;

      return true;
    } catch (error) {
      logException(error);
      console.error(
        "[FirebaseMessagingService] Error in requestPermissionAndSaveToken:",
        error
      );
      return false;
    }
  }

  public async getPermissionStatus(): Promise<string> {
    if (!Capacitor.isNativePlatform()) {
      return "granted"; // Non-native platforms don't need permissions
    }

    // Wait for initialization to complete
    await this.ensureInitialized();

    try {
      const permissions = await FirebaseMessaging.checkPermissions();
      return permissions.receive || "default";
    } catch (error) {
      logException(error);
      console.error(
        "[FirebaseMessagingService] Error checking permissions:",
        error
      );
      return "default";
    }
  }
}

// Export singleton instance
export const firebaseMessagingService = FirebaseMessagingService.getInstance();
